import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Mail, Users } from "lucide-react";
import { useProjectPermissions } from "@/app/capabilities/api/hooks";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { useGetProjectInvitations } from "@/app/invitations/api/hooks";
import { InviteMemberModal } from "@/app/invitations/components/modals/invite-member-modal";
import { ProjectInvitationsDashboard } from "@/app/invitations/components/project-invitations/project-invitations-dashboard";
import { useGetProjectMembers } from "@/app/members/api/hooks";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { useDashboardOrgProject } from "@/lib/org-project-context/hooks";
import { ProjectMembersDashboard } from "./components/project-members/project-members-dashboard";

// Each section fetches its data with a suspense query, so it must only mount when
// the caller holds the matching read permission — the /members and /invitations
// endpoints enforce members_read / invitations_read and 403 otherwise.
function MembersSection() {
  const membersCount = useGetProjectMembers().data?.count;

  return (
    <section className="rounded-xl border bg-card/50 p-4">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-tertiary/10 text-tertiary dark:bg-indigo-400/10 dark:text-indigo-400">
          <Users className="h-4 w-4" />
        </div>
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-base">
            Members
            {membersCount !== undefined && (
              <Badge variant="secondary">{membersCount}</Badge>
            )}
          </h2>
          <p className="text-muted-foreground text-sm">
            People with access to the project
          </p>
        </div>
      </div>
      <ProjectMembersDashboard showCountSummary={false} />
    </section>
  );
}

function PendingInvitationsSection() {
  const invitationsCount = useGetProjectInvitations().data?.items.length;

  return (
    <section className="rounded-xl border bg-card/50 p-4">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
          <Mail className="h-4 w-4" />
        </div>
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-base">
            Pending invitations
            {invitationsCount !== undefined && (
              <Badge variant="secondary">{invitationsCount}</Badge>
            )}
          </h2>
          <p className="text-muted-foreground text-sm">
            Invitations sent for this project
          </p>
        </div>
      </div>
      <ProjectInvitationsDashboard embedded />
    </section>
  );
}

export function MembersPage() {
  const { activeOrganization, activeProject } = useDashboardOrgProject();
  const { canProject } = useProjectPermissions();
  const canReadMembers = canProject(PROJECT.members.read);
  const canReadInvitations = canProject(PROJECT.invitations.read);

  // Title names only the sections the caller can actually see, so the header
  // never advertises a half that's gated off. The route guard guarantees at
  // least one read token, so the neutral fallback is only defensive.
  const visibleSections = [
    canReadMembers && "Members",
    canReadInvitations && "Invitations",
  ].filter(Boolean);
  const title =
    visibleSections.length > 0 ? visibleSections.join(" & ") : "Project Access";

  const description =
    activeOrganization && activeProject ? (
      <span>
        Manage access for{" "}
        <span className="font-medium text-foreground">
          {activeProject.name}
        </span>{" "}
        in{" "}
        <span className="font-medium text-foreground">
          {activeOrganization.name}
        </span>
      </span>
    ) : (
      "Manage who has access to this project and track pending invitations."
    );

  return (
    <ListLayout
      actions={
        <CanProject permission={PROJECT.invitations.create}>
          <InviteMemberModal />
        </CanProject>
      }
      description={description}
      title={title}
    >
      <div
        className={cn(
          "grid items-start gap-6",
          // Two columns only when both cards render; a lone card spans full width.
          canReadMembers && canReadInvitations && "xl:grid-cols-2"
        )}
      >
        <CanProject permission={PROJECT.members.read}>
          <MembersSection />
        </CanProject>
        <CanProject permission={PROJECT.invitations.read}>
          <PendingInvitationsSection />
        </CanProject>
      </div>
    </ListLayout>
  );
}
